import { useMemo, useState } from "react";
import SlideFrame from "./_SlideFrame";
import { fmt, PRICING } from "@/lib/api";
import * as Switch from "@radix-ui/react-switch";
import { Sparkles } from "lucide-react";

export default function SlideComplianceCalculator() {
  const [currentSpendMonthly, setCurrentSpendMonthly] = useState(60000);
  const [bundled, setBundled] = useState(false);

  const calc = useMemo(() => {
    const currentAnnual = currentSpendMonthly * 12;
    const complianceAnnual = PRICING.compliance_monthly * 12;
    const annualSavings = currentAnnual - complianceAnnual;
    const monthlySavings = currentSpendMonthly - PRICING.compliance_monthly;
    const pct = currentAnnual > 0 ? annualSavings / currentAnnual : 0;
    return { currentAnnual, complianceAnnual, annualSavings, monthlySavings, pct };
  }, [currentSpendMonthly]);

  return (
    <SlideFrame tag="06 · Compliance">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <p className="overline mb-4">Interactive</p>
          <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] font-medium max-w-4xl">
            How much you save by <em className="italic text-primary">bundling compliance</em>.
          </h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-sm">
          Compare what you'd spend building compliance in-house to our bundled monthly fee.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 border border-foreground/20 bg-white p-8">
          <p className="overline mb-6">Your current compliance spend</p>
          <label htmlFor="comp-spend" className="overline block mb-2">Monthly spend</label>
          <div className="flex items-baseline gap-2">
            <span className="font-mono-data text-muted-foreground">$</span>
            <input
              id="comp-spend"
              data-testid="input-compliance-spend"
              type="number"
              value={currentSpendMonthly}
              min={0} step={100}
              onChange={(e) => setCurrentSpendMonthly(parseInt(e.target.value, 10) || 0)}
              className="input-sharp flex-1"
            />
            <span className="font-mono-data text-xs text-muted-foreground whitespace-nowrap">/ month</span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground font-mono-data">
            Include CCO salary, compliance analysts, CMS licenses, audits, training.
          </p>

          <div className="mt-8 pt-6 border-t border-foreground/20 flex items-center justify-between">
            <div>
              <p className="overline">Bundle with IBA Servicing</p>
              <p className="text-sm text-muted-foreground mt-1">Servicing + compliance together</p>
            </div>
            <Switch.Root
              data-testid="bundle-toggle"
              checked={bundled}
              onCheckedChange={setBundled}
              className="w-12 h-6 bg-foreground/20 data-[state=checked]:bg-primary outline-none transition-colors relative"
            >
              <Switch.Thumb className="block w-5 h-5 bg-background translate-x-0.5 data-[state=checked]:translate-x-6 transition-transform" />
            </Switch.Root>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border-t-4 border-t-primary border-x border-b border-foreground/20 p-8 md:p-10">
            <p className="text-[11px] uppercase tracking-brand text-muted-foreground flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Bundled compliance savings
            </p>
            <div className="mt-4 grid md:grid-cols-2 gap-6">
              <div>
                <p className="overline mb-1">Annual savings</p>
                <p
                  data-testid="compliance-annual-savings"
                  className="font-mono-data text-5xl md:text-6xl tabular text-foreground"
                >
                  {fmt(bundled ? calc.annualSavings : 0)}
                </p>
              </div>
              <div>
                <p className="overline mb-1">Reduction</p>
                <p className="font-mono-data text-5xl md:text-6xl tabular text-primary">
                  {bundled ? (calc.pct * 100).toFixed(1) : "0.0"}%
                </p>
              </div>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              {bundled
                ? `Switch from ${fmt(currentSpendMonthly)}/mo to ${fmt(PRICING.compliance_monthly)}/mo — a saving of ${fmt(calc.monthlySavings)} every month.`
                : "Toggle the bundle on to see your savings."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-foreground/20 border border-foreground/20">
            <div className="bg-white p-6">
              <p className="overline mb-3">Your current stack · annual</p>
              <p className="font-mono-data text-3xl">{fmt(calc.currentAnnual)}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                {fmt(currentSpendMonthly)}/mo × 12
              </p>
            </div>
            <div className="bg-white p-6 border-l border-foreground/20">
              <p className="overline mb-3">CompliSun bundle · annual</p>
              <p className="font-mono-data text-3xl text-foreground">{fmt(calc.complianceAnnual)}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                {fmt(PRICING.compliance_monthly)}/mo × 12 · flat
              </p>
            </div>
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}
